import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { RiQrCodeFill } from "react-icons/ri";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PlateItemsColumns } from "../Page/PlateItemsColumns";
import PlateProductionModal from "./PlateProductionModal";
import {
  getPlateProductionListPath,
  getPlateProductionRowId,
} from "../utils/listSearchStorage";

const PLATE_PRODUCTION_BASE = `${process.env.REACT_APP_API_URL}/api/plates/plate-production`;
const PLATE_PRODUCTION_API = `${process.env.REACT_APP_API_URL}/api/plates/plate-production`;

const getPlatesFromItems = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  if (Array.isArray(payload.plates)) return payload.plates;
  if (Array.isArray(payload.Plates)) return payload.Plates;
  return [];
};

export const PlateProductionForm = ({ type }) => {
  const { id } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [showPlatesModal, setShowPlatesModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    addByCountModal: false,
    addByListModal: false,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const url = id && `${PLATE_PRODUCTION_BASE}/${id}/`;
  const itemsUrl =
    id && type === "info" ? `${PLATE_PRODUCTION_API}/${id}/items/` : null;
  const postUrl = `${PLATE_PRODUCTION_BASE}/`;
  const putUrl = `${PLATE_PRODUCTION_BASE}/${id}/`;
  const deleteUrl = `${PLATE_PRODUCTION_BASE}/${id}/`;
  const deletePlatesUrl = `${PLATE_PRODUCTION_BASE}/bulk-delete/`;

  const { data, loading, error, refetch } = useGet(url);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);

  const { error: postError, postData: postAction } = usePost(postUrl);
  const {
    data: putResponse,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);
  const {
    data: deletedPlatesData,
    error: deletedPlatesDataError,
    deleteMultiple: deletePlatesData,
  } = useDelete(deletePlatesUrl);

  const onFinish = async (values) => {
    try {
      if (id) {
        await putAction(values);
      } else {
        const result = await postAction(values);
        if (result != null) {
          openConfirmNotification("Success");
          setTimeout(() => {
            const rid = getPlateProductionRowId(result?.data ?? result);
            navigate(
              rid != null
                ? `/plate-production/info/${rid}`
                : getPlateProductionListPath(),
            );
          }, 200);
        }
      }
    } catch (err) {
      openErrorNotification("Something went wrong.");
      console.log(err);
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (err) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  const onDeletePlatesConfirm = async () => {
    try {
      await deletePlatesData({ ids: selectedRowKeys });
    } catch (err) {
      openErrorNotification("Something went wrong.");
    }
    setShowPlatesModal(false);
    setSelectedRowKeys([]);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  useEffect(() => {
    if (!id || loading) return;
    const load = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            ProductionName: String(data.data.ProductionName ?? ""),
            ProductionDesc: data.data.ProductionDesc
              ? String(data.data.ProductionDesc)
              : "",
          });
        }
      } catch (err) {
        if (!loading) {
          openErrorNotification("Error fetching data!");
        }
      }
    };

    load();
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putResponse) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(`/plate-production/info/${id}`);
      }, 200);
    }
  }, [putResponse, putLoading, putError, id, navigate, refetch]);

  useEffect(() => {
    if (error) {
      openErrorNotification("Not found");
      navigate(getPlateProductionListPath());
    }
  }, [error, navigate]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate(getPlateProductionListPath());
    }
    if (deletedPlatesData && !deletedPlatesDataError) {
      openConfirmNotification("Success");
      itemsRefetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedPlatesDataError) {
      openErrorNotification(
        deletedPlatesDataError?.response?.data?.message ||
          "Something went wrong",
      );
    }
  }, [
    deletedData,
    deletedPlatesData,
    deletedPlatesDataError,
    deletedDataError,
    navigate,
    itemsRefetch,
  ]);

  const breadcrumbItems = [
    {
      label: "Plate Production",
      href: "/plate-production",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/plate-production/info/${id}`
          : `/plate-production/edit/${id}`
        : "/plate-production/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/plate-production/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(getPlateProductionListPath());
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  const plateButtonsData = [
    {
      label: "Add by Count",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({
          ...prev,
          addByCountModal: true,
        }));
      },
    },
    {
      label: "Add by List",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({
          ...prev,
          addByListModal: true,
        }));
      },
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => {
        setShowPlatesModal(true);
      },
      disabled: selectedRowKeys?.length === 0,
    },
  ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <RiQrCodeFill className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Plate Production
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="ProductionName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="ProductionDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <ConfirmModal
        showModal={showPlatesModal}
        setShowModal={setShowPlatesModal}
        onDeleteConfirm={onDeletePlatesConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      {type === "info" && (
        <>
          <div className="flex justify-end mb-2">
            <CommonButton buttonsData={plateButtonsData} />
          </div>
          <GlobalTable
            columns={PlateItemsColumns()}
            dataSource={getPlatesFromItems(itemsData)
              .map((item) => ({
                ...item,
                key: item.PlateNo,
              }))
              .sort((a, b) => b.PlateNo - a.PlateNo)}
            rowSelection={rowSelection}
            onRow={() => ({})}
            loading={itemsLoading}
          />
          <PlateProductionModal
            isOpenModal={openModal.addByCountModal}
            setIsOpenModal={(value) =>
              setOpenModal((prev) => ({
                ...prev,
                addByCountModal: value,
              }))
            }
            platesRefetch={itemsRefetch}
            modalType="addByCount"
            title={
              <div className="flex items-center bg-gray-200 p-2 rounded-md">
                <RiQrCodeFill className="text-[18px] text-black " />
                <span className="text-[14px] font-[Poppins] text-black  ml-2">
                  Add by Count
                </span>
              </div>
            }
          />
          <PlateProductionModal
            isOpenModal={openModal.addByListModal}
            setIsOpenModal={(value) =>
              setOpenModal((prev) => ({
                ...prev,
                addByListModal: value,
              }))
            }
            platesRefetch={itemsRefetch}
            modalType="addByList"
            title={
              <div className="flex items-center bg-gray-200 p-2 rounded-md">
                <RiQrCodeFill className="text-[18px] text-black " />
                <span className="text-[14px] font-[Poppins] text-black  ml-2">
                  Add by List
                </span>
              </div>
            }
          />
        </>
      )}
    </>
  );
};
